import { appIntl } from '@components/locale/IntlGlobalProvider';
import {
  buildCreateEventOption,
  buildMeasureOption,
  buildQrCodeOption,
  CommonContextMenuContext,
} from '@components/map/contextMenu/builders/common';
import { createContextMenuDynamicOptions } from '@components/map/contextMenu/builders/utils';
import { FeatureTypeToContextComponentEnum, MenuBuilder, MenuKey } from '@components/map/contextMenu/type';

import { MapIdEnum, OperationalMapId } from '@/types/map.types';
import { MenuItemOption } from '@/types/menu.types';
import { SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

export type SensorContextMenuContextCore = {
  sensorType: SensorTypeEnum | SubSensorTypeEnum;
  isInfoOptionDisabled: boolean;
  isSensorMaintenance: boolean;
  hasRoleSensorMaintenance: boolean;
  isMaintenanceLoading: boolean;
  isMaintenanceOptionDisabled: boolean;
  isSensorActive: boolean;
  isActivationLoading: boolean;
  isHistoryOptionDisabled: boolean;
  isSubSensor: boolean;
  isRoundOptionHidden: boolean;
  isRoundOptionDisabled: boolean;
  handleOpenSensorInfo: () => void;
  toggleMaintenance: () => void;
  toggleActive: () => void;
  handleHistoryPopup: () => void;
  onRoundOptionAction: () => void;
};

export type AutomatonContextMenuContext = {
  isSleepAllCommandLoading: boolean;
  isPowerOnCabinetsOptionDisabled: boolean;
  isPowerOffCabinetsOptionDisabled: boolean;
  isAutomatonDefenceOptionLoading: boolean;
  isDisableCabinetsRepulsionOptionDisabled: boolean;
  isDisableCabinetsRepulsionOptionHidden: boolean;
  isEnableCabinetsRepulsionOptionDisabled: boolean;
  isEnableCabinetsRepulsionOptionHidden: boolean;
  onAutomatonSleepOptionAction: (sleep: boolean) => void;
  onAutomatonDefenceOptionAction: (defence: boolean) => void;
};

export type CabinetContextMenuContext = {
  isAckAlarmsLoading: boolean;
  isAckAlarmsDisabled: boolean;
  isCabinetConnectionEnabled: boolean;
  isCabinetConnectionOptionLoading: boolean;
  isCabinetConnectionOptionDisabled: boolean;
  isCabinetRepulsionOptionLoading: boolean;
  isDisableSegmentsRepulsionDisabled: boolean;
  isDisableSegmentsRepulsionHidden: boolean;
  isEnableSegmentsRepulsionDisabled: boolean;
  isEnableSegmentsRepulsionHidden: boolean;
  onAckAlarmsAction: () => void;
  onCabinetConnectionOptionAction: () => void;
  onCabinetRepulsionOptionAction: (arg: boolean) => void;
};

export type SensorContextMenuContext<T extends SensorTypeEnum | SubSensorTypeEnum | undefined> =
  T extends SensorTypeEnum.AUTOMATON
    ? CommonContextMenuContext & SensorContextMenuContextCore & AutomatonContextMenuContext
    : T extends SubSensorTypeEnum.AUTOMATON_CABINET
      ? CommonContextMenuContext & SensorContextMenuContextCore & CabinetContextMenuContext
      : CommonContextMenuContext & SensorContextMenuContextCore;

export type SensorContextMenuStaticOptions = {
  info: boolean;
  history: boolean;
};

export type SensorContextMenuDynamicOptionsCore = {
  maintenance: boolean;
  connection: boolean;
  round: boolean;
};
type AutomatonContextMenuOptions = SensorContextMenuDynamicOptionsCore & {
  powerOnCabinets: boolean;
  powerOffCabinets: boolean;
  disableCabinetsRepulsion: boolean;
  enableCabinetsRepulsion: boolean;
};
type AutomatonCabinetContextMenuOptions = SensorContextMenuDynamicOptionsCore & {
  ackAlarms: boolean;
  cabinetConnection: boolean;
  disableSegmentsRepulsion: boolean;
  enableSegmentsRepulsion: boolean;
};
export type SensorContextMenuDynamicOptions =
  | SensorContextMenuDynamicOptionsCore
  | AutomatonContextMenuOptions
  | AutomatonCabinetContextMenuOptions;

export type SensorContextMenuOptions = SensorContextMenuStaticOptions & SensorContextMenuDynamicOptions;

export const SENSOR_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS: SensorContextMenuDynamicOptions = {
  maintenance: true,
  connection: true,
  round: false,
  powerOnCabinets: false,
  powerOffCabinets: false,
  disableCabinetsRepulsion: false,
  enableCabinetsRepulsion: false,
  ackAlarms: false,
  cabinetConnection: false,
  disableSegmentsRepulsion: false,
  enableSegmentsRepulsion: false,
};

const allTrue = createContextMenuDynamicOptions(SENSOR_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, true);
const allFalse = createContextMenuDynamicOptions(SENSOR_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, false);
export const sensorContextMenuConfig: Record<OperationalMapId, SensorContextMenuDynamicOptions> = {
  [MapIdEnum.LIVE]: allTrue,
  [MapIdEnum.LAD]: allTrue,
  [MapIdEnum.REPLAY]: allFalse,
  [MapIdEnum.STATISTICS]: allFalse,
};

const buildOpenSensorInfoOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => {
  const formatMessage = appIntl().formatMessage;
  return [
    'info',
    {
      label: formatMessage(
        {
          id: 'contextmenu.actions.sensor.sensorInfo',
        },
        {
          type: formatMessage(
            { id: `contextmenu.actions.type` }, //TODO : maybe dans actions.sensor.type
            {
              type: ctx.sensorType,
            },
          ),
        },
      ),
      shouldNotTranslate: true,
      isDisabled: ctx.isInfoOptionDisabled,
      onAction: ctx.handleOpenSensorInfo,
    },
  ];
};

const buildMaintenanceOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'maintenance',
  {
    label: `contextmenu.actions.${ctx.isSensorMaintenance ? 'maintenanceOff' : 'maintenanceOn'}`,
    isHidden: !ctx.hasRoleSensorMaintenance,
    isLoading: ctx.isMaintenanceLoading,
    isDisabled: ctx.isMaintenanceOptionDisabled,
    onAction: ctx.toggleMaintenance,
  },
];

const buildActivationOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => {
  const formatMessage = appIntl().formatMessage;
  return [
    'connection',
    {
      label: formatMessage({ id: 'contextmenu.actions.connect' }, { connected: ctx.isSensorActive }),
      isHidden: !ctx.hasRoleSensorMaintenance,
      isLoading: ctx.isActivationLoading,
      shouldNotTranslate: true,
      onAction: ctx.toggleActive,
    },
  ];
};

const buildRoundOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => [
  'round',
  {
    label: 'contextmenu.actions.round',
    isHidden: ctx.isRoundOptionHidden,
    isDisabled: ctx.isRoundOptionDisabled,
    onAction: ctx.onRoundOptionAction,
  },
];

const buildPowerOnCabinetsOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const automatonCtx = ctx as SensorContextMenuContext<SensorTypeEnum.AUTOMATON>;
  return [
    'powerOnCabinets',
    {
      label: 'contextmenu.actions.automaton.sleep.on',
      isLoading: automatonCtx.isSleepAllCommandLoading,
      isDisabled: automatonCtx.isPowerOnCabinetsOptionDisabled,
      onAction: () => automatonCtx.onAutomatonSleepOptionAction(true),
    },
  ];
};

const buildPowerOffCabinetsOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const automatonCtx = ctx as SensorContextMenuContext<SensorTypeEnum.AUTOMATON>;
  return [
    'powerOffCabinets',
    {
      label: 'contextmenu.actions.automaton.sleep.off',
      isLoading: automatonCtx.isSleepAllCommandLoading,
      isDisabled: automatonCtx.isPowerOffCabinetsOptionDisabled,
      onAction: () => automatonCtx.onAutomatonSleepOptionAction(false),
    },
  ];
};

const buildDisableCabinetsRepulsionOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const automatonCtx = ctx as SensorContextMenuContext<SensorTypeEnum.AUTOMATON>;
  return [
    'disableCabinetsRepulsion',
    {
      label: 'contextmenu.actions.automaton.defence.detection',
      isLoading: automatonCtx.isAutomatonDefenceOptionLoading,
      isDisabled: automatonCtx.isDisableCabinetsRepulsionOptionDisabled,
      isHidden: automatonCtx.isDisableCabinetsRepulsionOptionHidden,
      onAction: () => automatonCtx.onAutomatonDefenceOptionAction(false),
    },
  ];
};

const buildEnableCabinetsRepulsionOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const automatonCtx = ctx as SensorContextMenuContext<SensorTypeEnum.AUTOMATON>;
  return [
    'enableCabinetsRepulsion',
    {
      label: 'contextmenu.actions.automaton.defence.repulsion',
      isLoading: automatonCtx.isAutomatonDefenceOptionLoading,
      isDisabled: automatonCtx.isEnableCabinetsRepulsionOptionDisabled,
      isHidden: automatonCtx.isEnableCabinetsRepulsionOptionHidden,
      onAction: () => automatonCtx.onAutomatonDefenceOptionAction(true),
    },
  ];
};

const buildAckAlarmsOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const cabinetCtx = ctx as SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET>;
  return [
    'ackAlarms',
    {
      label: 'contextmenu.actions.cabinet.ackAlarms',
      isLoading: cabinetCtx.isAckAlarmsLoading,
      isDisabled: cabinetCtx.isAckAlarmsDisabled,
      onAction: cabinetCtx.onAckAlarmsAction,
    },
  ];
};

const buildCabinetConnectionOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const cabinetCtx = ctx as SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET>;
  return [
    'cabinetConnection',
    {
      label: `contextmenu.actions.cabinet.sleep.${cabinetCtx.isCabinetConnectionEnabled ? 'on' : 'off'}`,
      isLoading: cabinetCtx.isCabinetConnectionOptionLoading,
      isDisabled: cabinetCtx.isCabinetConnectionOptionDisabled,
      onAction: cabinetCtx.onCabinetConnectionOptionAction,
    },
  ];
};

const buildDisableSegmentsRepulsionOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const cabinetCtx = ctx as SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET>;
  return [
    'disableSegmentsRepulsion',
    {
      label: 'contextmenu.actions.cabinet.defence.detection',
      isLoading: cabinetCtx.isCabinetRepulsionOptionLoading,
      isDisabled: cabinetCtx.isDisableSegmentsRepulsionDisabled,
      isHidden: cabinetCtx.isDisableSegmentsRepulsionHidden,
      onAction: () => cabinetCtx.onCabinetRepulsionOptionAction(false),
    },
  ];
};

const buildEnableSegmentsRepulsionOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (ctx) => {
  const cabinetCtx = ctx as SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET>;
  return [
    'enableSegmentsRepulsion',
    {
      label: 'contextmenu.actions.cabinet.defence.repulsion',
      isLoading: cabinetCtx.isCabinetRepulsionOptionLoading,
      isDisabled: cabinetCtx.isEnableSegmentsRepulsionDisabled,
      isHidden: cabinetCtx.isEnableSegmentsRepulsionHidden,
      onAction: () => cabinetCtx.onCabinetRepulsionOptionAction(true),
    },
  ];
};

const buildHistoryOption: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'history',
  {
    label: `contextmenu.actions.sensorHistory`,
    isHidden: ctx.isSubSensor,
    isDisabled: ctx.isHistoryOptionDisabled,
    onAction: ctx.handleHistoryPopup,
  },
];

const sensorBuildersCore: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>[] = [
  {
    order: 1,
    build: buildOpenSensorInfoOption,
  },
  {
    order: 2,
    build: buildQrCodeOption,
  },
  {
    order: 3,
    build: buildMeasureOption,
  },
  {
    order: 4,
    build: buildCreateEventOption,
  },
  {
    order: 5,
    separator: true,
  },
  {
    order: 6,
    build: buildMaintenanceOption,
  },
  {
    order: 7,
    build: buildActivationOption,
  },
  {
    order: 8,
    build: buildRoundOption,
  },
  {
    order: 17,
    separator: true,
  },
  {
    order: 18,
    build: buildHistoryOption,
  },
];

const sensorBuildersAutomaton: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>[] = [
  {
    order: 9,
    build: buildPowerOnCabinetsOption,
  },
  {
    order: 10,
    build: buildPowerOffCabinetsOption,
  },
  {
    order: 11,
    build: buildDisableCabinetsRepulsionOption,
  },
  {
    order: 12,
    build: buildEnableCabinetsRepulsionOption,
  },
];

const sensorBuildersCabinet: MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>[] = [
  {
    order: 13,
    build: buildAckAlarmsOption,
  },
  {
    order: 14,
    build: buildCabinetConnectionOption,
  },
  {
    order: 15,
    build: buildDisableSegmentsRepulsionOption,
  },
  {
    order: 16,
    build: buildEnableSegmentsRepulsionOption,
  },
];

const isAutomatonCtx = (
  ctx: SensorContextMenuContext<SensorTypeEnum | SubSensorTypeEnum | undefined>,
): ctx is SensorContextMenuContext<SensorTypeEnum.AUTOMATON> => {
  return 'isPowerOnCabinetsOptionDisabled' in ctx;
};

const isCabinetCtx = (
  ctx: SensorContextMenuContext<SensorTypeEnum | SubSensorTypeEnum | undefined>,
): ctx is SensorContextMenuContext<SubSensorTypeEnum.AUTOMATON_CABINET> => {
  return 'isCabinetConnectionEnabled' in ctx;
};

export const getSensorBuilders = (
  ctx: SensorContextMenuContext<SensorTypeEnum | SubSensorTypeEnum | undefined>,
): MenuBuilder<FeatureTypeToContextComponentEnum.SENSOR>[] => {
  if (isAutomatonCtx(ctx)) {
    return [...sensorBuildersCore, ...sensorBuildersAutomaton];
  } else if (isCabinetCtx(ctx)) {
    return [...sensorBuildersCore, ...sensorBuildersCabinet];
  }
  return sensorBuildersCore;
};
